import API from '@/utils/API';
import productSelectionParser from '@/utils/product-selection-parser';
import { ServiceUnavailableError } from '@/utils/API/core/exceptions';
import humps from 'humps';
import retry from 'async/retry';

/** Steps */
export const WELCOME_STEP = 'WELCOME_STEP';
export const BUY_MORTGAGE_CONDITIONS_STEP = 'BUY_MORTGAGE_CONDITIONS_STEP';
export const NEW_MORTGAGE_CONDITIONS_STEP = 'NEW_MORTGAGE_CONDITIONS_STEP';
export const MORE_INFO_STEP = 'MORE_INFO_STEP';
export const CUSTOMER_FORM_STEP = 'CUSTOMER_FORM_STEP';
export const PERSONAL_DATA_RECEIVED_STEP = 'PERSONAL_DATA_RECEIVED_STEP';
export const PROPERTY_FORM_STEP = 'PROPERTY_FORM_STEP';
export const INCOMES_CATEGORY_STEP = 'INCOMES_CATEGORY_STEP';
export const INCOMES_FORM_STEP = 'INCOMES_FORM_STEP';
export const MORTGAGE_FORM_STEP = 'MORTGAGE_FORM_STEP';
export const SUCCESS_STEP = 'SUCCESS_STEP';
export const NO_INCOMES_SUPPORT_STEP = 'NO_INCOMES_SUPPORT_STEP';
export const ERROR_STEP = 'ERROR_STEP';

/** Choices */
export const BUY_MORTGAGE_CHOICE = 'BUY_MORTGAGE_CHOICE';
export const NEW_MORTGAGE_CHOICE = 'NEW_MORTGAGE_CHOICE';
export const CONFIRM_CHOICE = 'CONFIRM_CHOICE';
export const DECLINE_CHOICE = 'DECLINE_CHOICE';

/** Trust Banners */
import TrustBanks from './components/trust/trust-banks';
import TrustMedios from './components/trust/trust-medios';

/** Form Components **/
import CustomerFormComponent from './components/forms/customer-form';
import MortgageFormComponent from './components/forms/mortgage-form';
import PropertyFormComponent from './components/forms/property-form';
import IncomesFormComponent from './components/forms/incomes-form';

import { MERGE_DATA } from '@/vuex/modules/flower';
import { userAccountURL, webSiteURL, blogURL, cloudfrontURL } from '@/utils/url-builder';
import {
  NEW_MORTGAGE_SLUG,
  BUY_MORTGAGE_SLUG,
  HOME_EQUITY_LOAN_SLUG,
} from '@/utils/constants';

const redirectToAccount = store => {
  const data = store.state.data;

  const url = data?.loginURL || userAccountURL(
    '/login',
    {
      queryParams: {
        email: data?.customer?.email,
        dni: data?.customer?.dni,
      },
    },
  );

  window.location.href = url;
}

const nextStepFromProductSelection = product => {
  switch (product) {
    case NEW_MORTGAGE_SLUG:
      return NEW_MORTGAGE_CONDITIONS_STEP;
    case BUY_MORTGAGE_SLUG:
      return BUY_MORTGAGE_CONDITIONS_STEP;
    default:
      return false;
  }
};

const steps = {
  [WELCOME_STEP]: {
    question: {
      title: '¿Cómo podemos ayudarte?',
      description: '<span class="has-text-primary">Te asesoramos gratuitamente</span> en alguna de las siguientes soluciones',
    },
    choice({ store }) {

      return {
        component: 'product-choice',
        default: { value: productSelectionParser.stringify(store.state.data.product, store.state.data.motive) },
        items: [
          {
            title: 'Quiero <b>trasladar</b> mi crédito hipotecario',
            value: productSelectionParser.stringify(BUY_MORTGAGE_SLUG),
          },
          {
            title: 'Quiero <b>comprar</b> un inmueble',
            value: productSelectionParser.stringify(NEW_MORTGAGE_SLUG)
          },
        ],
      };
    },
    mounted({ store }) {
      const product = store.state.data?.product;

      store.dispatch('trust/setBanner', TrustBanks, { root: true });

      if(!store.state.data?.product) return;

      const stepName = nextStepFromProductSelection(product)

      if(stepName)
        store.dispatch('goToStep', {
          step: getStepByName(nextStepFromProductSelection(product)),
        });
    },
    beforeNext({ store }) {
      const { product, motive } = productSelectionParser.parse(store.getters.lastAnswer.value);

      store.commit(MERGE_DATA, { product, motive });
    },
    nextStep: ({ store: { getters: { lastAnswer: { value }}} }) => {
      const { product } = productSelectionParser.parse(value);

      return nextStepFromProductSelection(product);
    },
  },

  [BUY_MORTGAGE_CONDITIONS_STEP]: {
    progress: 5,
    question: ({ store: { rootGetters } }) => ({
      title: `¡Excelente! Mi nombre es ${rootGetters['adviser/adviser'].name} y te voy a guiar en este proceso. Toma en cuenta que:`,
      description: 'El saldo deudor debe ser mayor a $5 000 000',
    }),
    choice: {
      items: [
        { title: 'Si, continuemos', value: CONFIRM_CHOICE },
        { title: 'No, no cumplo con esos requisitos', value: DECLINE_CHOICE },
      ],
    },
    nextStep({ store: { state: { lastAnswer: { value }}} }) {
      switch (value) {
        case CONFIRM_CHOICE:
          return PROPERTY_FORM_STEP;
        case DECLINE_CHOICE:
          return MORE_INFO_STEP;
      }
    },
  },

  [NEW_MORTGAGE_CONDITIONS_STEP]: {
    progress: 5,
    question: ({ store: { rootGetters } }) => ({
      title: `¡Excelente! Mi nombre es ${rootGetters['adviser/adviser'].name} y te voy a guiar en este proceso 👍`,
      description: 'Es necesario que puedas sustentar tus ingresos para la evaluacion del crédito',
    }),
    choice: {
      items: [
        { title: 'Si, continuemos', value: CONFIRM_CHOICE },
        { title: 'No, no tengo cómo sustentar mis ingresos', value: DECLINE_CHOICE },
      ],
    },
    nextStep({ store: { state: { lastAnswer: { value }}} }) {
      switch (value) {
        case CONFIRM_CHOICE:
          return PROPERTY_FORM_STEP;
        case DECLINE_CHOICE:
          return NO_INCOMES_SUPPORT_STEP;
      }
    }
  },

  [MORE_INFO_STEP]: {
    question: {
      title: '¿Te gustaría que te ayudemos en algo más?',
    },
    choice: {
      items: [
        { title: 'No, eso es todo', value: DECLINE_CHOICE, href: webSiteURL() },
        { title: 'Leer nuestro blog', value: 'BLOG', href: blogURL() },
      ],
    },
  },


  get [NO_INCOMES_SUPPORT_STEP]() {
    return {
      ...this[MORE_INFO_STEP],
      question: {
        title: 'Si no tienes cómo sustentar ingresos, no vamos a poder ayudarte por el momento 😢',
        description: '¿Cómo te gustaríá continuar?',
      },
    };
  },

  [CUSTOMER_FORM_STEP]: {
    progress: 81,

    question: {
      title: 'Crea una cuenta con nosotros',
      description: 'En tu cuenta vas a poder hacer seguimiento a tu crédito ver si calificas y las propuestas que tenemos para ti.',
    },

    form: {
      component: CustomerFormComponent,
      dataKey: 'customer',
    },

    mounted({ store }) {
      store.dispatch('trust/setBanner', TrustMedios, { root: true });
    },

    async beforeNext({ store }) {
      const { commit } = store;
      const formData = store.getters.lastAnswer;
      const propertyFormData = store.state.data.property;
      const mortgageFormData = store.state.data.mortgage;
      const incomeCategory = store.state.answers[INCOMES_CATEGORY_STEP];
      const product = store.state.data.product;
      const motive = store.state.data.motive;
      const incomesFormData = store.state.data.income;
      const adviser = store.rootGetters['adviser/adviser'];

      store.dispatch('removeNotification');

      try {
        const searchParams = new URLSearchParams(window.location.search);
        const currencyId = 3;
        const requestData = {
          adviserId: adviser.id,
          first_name: formData.firstName,
          email: formData.email,
          vat: formData.vat,
          product,
          motive,
          mobile: formData.mobile,
          source: searchParams.get('utm_source'),
          medium: searchParams.get('utm_medium'),
          campaign: searchParams.get('utm_campaign'),
          referrerId: searchParams.get('referrer_id'),
          createDeal: true,
          showReport: false,
          //--- Property
          property: {
            value: propertyFormData.value,
            currencyId,
          },
          //--- Credit
          ...mortgageFormData ? {
            credit: {
              bankId: mortgageFormData.bank?.id,
              simpleFee: mortgageFormData.simpleFee,
              months: mortgageFormData.months,
              credit: mortgageFormData.credit,
              currencyId,
            },
          } : {},
          //--- Incomes
          incomes: [
            {
              amount: incomesFormData.amount,
              currencyId,
              incomeTypeId: incomeCategory.value,
            },
          ],
        };

        return await retry(
          {
            times: 5,
            interval: 2000,
            errorFilter(err) {
              return err instanceof ServiceUnavailableError;
            },
          },
          (cb) => {
            API.form(humps.decamelizeKeys(requestData))
              .then(({ data: responseData, status }) => {
                if(status === 200) {
                  commit(MERGE_DATA, {
                    token: responseData.token,
                    loginURL: responseData.loginUrl,
                    firstName: responseData.firstName,
                    lastName: responseData.lastName,
                    propertyValue: parseInt(responseData?.propertyValue) || 0,
                  });

                  API.headers({ Authorization: `JWT ${responseData.token}` });
                } else if (status === 202){
                  store.dispatch('addNotification', { type: 'danger', body: `Al parecer ya tienes una cuenta con nosotros. <a href="${userAccountURL('/login')}">Haz click aquí</a> para ingresar a tu cuenta y continuar con tu solicitud.`});

                  return false;
                } else if(status === 209) {
                  store.dispatch('addNotification', { type: 'danger', body: `El DNI que nos proporcionaste no tiene una hipoteca inscrita según la información que nos proporciona las centrales de riesgos. Comunícate con nosotros para continuar al <a href="tel:+57-1-4896848">1-4896848</a> o al correo <a href="mailto:ayuda@rebajatuscuentas.com">ayuda@rebajatuscuentas.com</a>.`});

                  return false;
                } else if(status === 503) {
                  return new ServiceUnavailableError();
                } else {
                  return new Error('Ha ocurrido un error desconocido');
                }
              })
              .catch((error) => cb(error));
          }
        );
      } catch(error) {
        return ERROR_STEP;
      }
    },
    nextButtonText: 'Crear cuenta gratuitamente',
    nextStep: SUCCESS_STEP,

  },

  [PROPERTY_FORM_STEP]: {
    progress: 50,
    question({ store }) {
      const product = store.state.data.product;

      return {
        title: do{
          if(product === NEW_MORTGAGE_SLUG) '¿Cuánto vale la propiedad que quieres comprar?';
          else if(product === BUY_MORTGAGE_SLUG) '¿Cuánto vale la propiedad asociado al crédito hipotecario?';
          else if(product === HOME_EQUITY_LOAN_SLUG) '¿Cuánto vale la propiedad que quieres dejar en garantía?';
          else '¿Cual es el valor de la propiedad?';
        },
      };
    },

    form: {
      component: PropertyFormComponent,
      dataKey: 'property',
    },

    nextStep({ store }) {
      const product = store.state.data.product;

      if([NEW_MORTGAGE_SLUG, HOME_EQUITY_LOAN_SLUG].includes(product)) {
        return INCOMES_CATEGORY_STEP;
      } else if(product === BUY_MORTGAGE_SLUG) {
        return MORTGAGE_FORM_STEP;
      }
    },
  },

  [MORTGAGE_FORM_STEP]: {
    progress: 60,

    question: {
      title: 'Información sobre el crédito actual',
      description: 'Necesitamos información lo más precisa de tu crédito actual. Esto nos permitirá mostrar propuestas de ahorro.',
    },

    form: {
      component: MortgageFormComponent,
      dataKey: 'mortgage',
    },

    nextButtonText: 'Continuar a los últimos pasos',

    nextStep: INCOMES_CATEGORY_STEP,
  },

  [INCOMES_CATEGORY_STEP]: {
    progress: 70,
    question: {
      title: '¿Cómo percibes tus ingresos?',
      description: 'Coloca la categoría principal de cómo percibes tus ingresos.',
    },
    choice: {
      items: [
        { title: 'Empleado', text: 'Empleado', value: 1, icon: cloudfrontURL('/icons/income-categories/employee.svg')  },
        { title: 'Independiente', text: 'Trabajador Independiente, Recibo por Honorarios.', value: 2, icon: cloudfrontURL('/icons/income-categories/services.svg')  },
        { title: 'Pensionado', text: 'Pensionado', value: 3, icon: cloudfrontURL('/icons/income-categories/other.svg')  },
        { title: 'Rentista', text: 'Rentista', value: 4, icon: cloudfrontURL('/icons/income-categories/rent.svg') },
        { title: 'Prestación de servicios', text: 'Prestación de servicios', value: 5, icon: cloudfrontURL('/icons/income-categories/business.svg')  },
        { title: 'Transportador', text: 'Transportador', value: 6, icon: cloudfrontURL('/icons/income-categories/taxi.svg')  },
      ],
      scrollOnSelect: true,
    },
    nextStep: INCOMES_FORM_STEP,
  },

  [INCOMES_FORM_STEP]: {
    progress: 71,
    question: {
      title: 'Nos falta conocer tus ingresos',
      description: 'Coloca el total de ingresos brutos del titular del crédito. Tus datos son protegidos y cifrados 🔒',
    },

    form: {
      component: IncomesFormComponent,
      dataKey: 'income',
    },

    nextButtonText: 'Ver pre-calificación y propuestas',

    nextStep: CUSTOMER_FORM_STEP,
  },

  [ERROR_STEP]: {
    question: {
      title: 'Ha ocurrido un error',
      description: 'Por favor comunícate con nosotros para poder ayudarte al <a href="tel:+57-1-4896848">1-4896848</a> o al correo <a href="mailto:ayuda@rebajatuscuentas.com">ayuda@rebajatuscuentas.com</a>.'
    },
  },

  [SUCCESS_STEP]: {
    progress: 100,
    question: {
      title: '¡Felicidades!',
      description: 'Ahora vamos a pre-calificarte y mostrarte propuestas de crédito para tu solicitud. <br><br>' +
        'Serás redireccionado en 3 segundos',
    },

    mounted({ store }) {
      setTimeout(() => {
        redirectToAccount(store);
      }, 4000);
    },
  },
};

export const getStepByName = name => steps[name];

export default steps;